import React from 'react';
import cx from 'classnames';

import { Select, SelectItem, Switch } from '@heroui/react';

import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import { FieldInfo } from '@/components/Common/Dropdown';

// ---------------------------------------------------------------------------
// Shared look for the card-style settings pages (Settings -> System).
// The page is a light canvas carrying white cards; every card has an icon
// badge, a title, a subtitle and an optional status pill on the right.
// ---------------------------------------------------------------------------

export const su = {
  // Block-level on purpose: `min-h` on a column flex container would hand the
  // leftover height to the cards and stretch them.
  page:			'bg-[#F4F6F9] min-h-[calc(100vh-64px)] w-full px-[32px] pt-[24px] pb-[32px]',
  pageInner:	'w-full max-w-[1040px] space-y-[20px]',
  card:			'bg-white rounded-[10px] border-solid border-1 border-[#E6E9EF] w-full',
  cardHead:		'px-[24px] py-[18px] gap-[14px]',
  cardHeadLine:	'border-b-1 border-solid border-[#EEF0F4]',
  cardBody:		'px-[24px] py-[20px] gap-[20px]',
  badge:		'w-[36px] h-[36px] rounded-[8px] bg-[#EEF2F8] flex items-center justify-center shrink-0',
  badgeIcon:	'material-icons !text-[--Primetime-main] !text-[20px]',
  title:		'text-[16px] font-semibold text-[#1F2733] leading-[22px]',
  titleSoft:	'text-[16px] font-normal text-[#6B7280]',
  subtitle:		'text-[13px] text-[#6B7280] leading-[18px]',
  hint:			'text-[12px] text-[#6B7280] leading-[16px]',
  pageTitle:	'text-[26px] font-semibold text-[#1F2733] leading-[34px]',
};

export type PillTone = 'ok' | 'bad' | 'info' | 'warn';

const pillTone: { [tone in PillTone]: string } = {
  ok:		'bg-[#E9F5EE] text-[#2E7D4F]',
  bad:		'bg-[#FDECEA] text-[#B14545]',
  info:		'bg-[#EEF2F8] text-[#3A5A8C]',
  warn:		'bg-[#FDF4E3] text-[#8A6A2F]',
};

const pillDot: { [tone in PillTone]: string } = {
  ok:		'bg-[#3BA05F]',
  bad:		'bg-[#D64545]',
  info:		'bg-[#3A5A8C]',
  warn:		'bg-[#B98B36]',
};

export interface PillProps {
  tone:			PillTone;
  label:		string;
  dot?:			boolean;
  icon?:		string;
}

export const Pill = (props: PillProps) => (
  <span className={cx(
          'inline-flex items-center gap-[6px] rounded-full px-[10px] py-[4px]',
          'text-[12px] font-medium whitespace-nowrap',
          pillTone[props.tone],
        )}>
    { props.dot &&
      <span className={cx('w-[6px] h-[6px] rounded-full shrink-0', pillDot[props.tone])} />
    }
    { props.icon &&
      <span className="material-icons !text-[14px]">{ props.icon }</span>
    }
    { props.label }
  </span>
);

export interface SettingsCardProps {
  icon:			string;
  title:		string;
  titleSuffix?:	string;
  subtitle?:	string;
  status?:		React.ReactNode;
  children?:	React.ReactNode;
  bodyClassName?:	string;
}

export const SettingsCard = (props: SettingsCardProps) => (
  <FlexRow direction="column" align="start" className={su.card}>
    <FlexRow align="start"
             className={cx(su.cardHead, props.children ? su.cardHeadLine : '')}>
      <div className={su.badge}>
        <span className={su.badgeIcon}>{ props.icon }</span>
      </div>
      <FlexRow direction="column" align="start" flex={1} className="gap-[2px]">
        <div>
          <span className={su.title}>{ props.title }</span>
          { props.titleSuffix &&
            <span className={su.titleSoft}> { props.titleSuffix }</span>
          }
        </div>
        { props.subtitle &&
          <div className={su.subtitle}>{ props.subtitle }</div>
        }
      </FlexRow>
      { props.status &&
        <FlexRow flex={0} className="pt-[4px]">
          { props.status }
        </FlexRow>
      }
    </FlexRow>
    { props.children &&
      <FlexRow direction="column" align="start" className={cx(su.cardBody, props.bodyClassName)}>
        { props.children }
      </FlexRow>
    }
  </FlexRow>
);

// A row-style switch: toggle on the left, caption + description stacked next
// to it. Used for the 2FA gate and the per-instance disable flag.
export interface ToggleRowProps {
  value:		boolean;
  setValue:		(val: boolean) => any;
  caption:		string;
  description?:	string;
  isDisabled?:	boolean;
}

export const ToggleRow = (props: ToggleRowProps) => (
  <FlexRow align="start" className="gap-[16px] w-full">
    <Switch isSelected={props.value}
            isDisabled={props.isDisabled}
            size="sm"
            aria-label={props.caption}
            classNames={{
              base:		'shrink-0',
              wrapper:	'mr-0 group-data-[selected=true]:bg-[--Primetime-main]',
            }}
            onValueChange={props.setValue} />
    <FlexRow direction="column"
             align="start"
             flex={1}
             className="gap-[2px] cursor-pointer select-none -mt-[2px]"
             onClick={() => !props.isDisabled && props.setValue(!props.value)}>
      <span className="text-[14px] font-medium text-[#1F2733]">
        { props.caption }
      </span>
      { props.description &&
        <span className={su.subtitle}>{ props.description }</span>
      }
    </FlexRow>
  </FlexRow>
);

// A field whose label sits notched into the top border of the control, the
// way the rest of this page's inputs are drawn.
export interface LabeledFieldProps {
  label:		string;
  required?:	boolean;
  invalid?:		boolean;
  error?:		string;
  hint?:		string;
  className?:	string;
  children:		React.ReactNode;
}

export const LabeledField = (props: LabeledFieldProps) => (
  <FlexRow direction="column"
           align="start"
           className={cx('gap-[6px] w-full', props.className)}>
    <div className="relative w-full">
      <span className={cx(
              'absolute -top-[8px] left-[12px] z-20 px-[6px] bg-white',
              'text-[12px] leading-[16px] pointer-events-none whitespace-nowrap',
              props.invalid ? 'text-[--Error]' : 'text-[#6B7280]',
            )}>
        { props.label }{ props.required ? ' *' : '' }
      </span>
      { props.children }
    </div>
    { props.invalid && props.error &&
      <span className="text-[12px] text-[--Error] leading-[16px]">
        { props.error }
      </span>
    }
    { !(props.invalid && props.error) && props.hint &&
      <span className={su.hint}>{ props.hint }</span>
    }
  </FlexRow>
);

// `!` throughout: Common/Input and HeroUI's bordered Select both ship their own
// radius / padding / border-width on these slots, and an un-flagged arbitrary
// value would win or lose purely on stylesheet order.
const controlBase = [
  'h-[52px] !rounded-[6px] !bg-white !px-[14px]',
  'border-solid !border-1',
  'data-[hover=true]:!border-[#B9C0CC]',
  'data-[focus=true]:!border-[--Primetime-main]',
  'data-[open=true]:!border-[--Primetime-main]',
].join(' ');

const controlBorder = (invalid?: boolean) =>
  controlBase + (invalid ? ' !border-[--Error]' : ' !border-[#D8DCE3]');

export interface SettingsInputProps {
  label:		string;
  value:		string;
  setValue:		(val: string) => any;
  required?:	boolean;
  invalid?:		boolean;
  error?:		string;
  hint?:		string;
  placeholder?:	string;
  type?:		string;
  isLoaded?:	boolean;
  className?:	string;
  // For secrets: a cloud spell-checker can upload what is typed, and browsers
  // ignore autoComplete="off" on a password field ("new-password" works).
  autoComplete?:	string;
  spellCheck?:	boolean;
}

export const SettingsInput = (props: SettingsInputProps) => (
  <LabeledField label={props.label}
                required={props.required}
                invalid={props.invalid}
                error={props.error}
                hint={props.hint}
                className={props.className}>
    <Input aria-label={props.label}
           type={props.type}
           value={props.value}
           placeholder={props.placeholder}
           isInvalid={props.invalid}
           isLoaded={props.isLoaded}
           autoComplete={props.autoComplete}
           spellCheck={props.spellCheck === undefined ? undefined : props.spellCheck ? 'true' : 'false'}
           classNames={{
             inputWrapper:	controlBorder(props.invalid),
             input:			'text-[15px] text-[#1F2733] placeholder:text-[#A3AAB7]',
           }}
           onValueChange={props.setValue} />
  </LabeledField>
);

export interface SettingsSelectProps {
  label:		string;
  value:		string;
  setValue:		(val: string) => any;
  options:		FieldInfo[];
  required?:	boolean;
  invalid?:		boolean;
  error?:		string;
  hint?:		string;
  isLoaded?:	boolean;
  className?:	string;
}

export const SettingsSelect = (props: SettingsSelectProps) => (
  <LabeledField label={props.label}
                required={props.required}
                invalid={props.invalid}
                error={props.error}
                hint={props.hint}
                className={props.className}>
    <Select aria-label={props.label}
            variant="bordered"
            selectionMode="single"
            disallowEmptySelection={true}
            selectedKeys={props.value ? [props.value] : []}
            isInvalid={props.invalid}
            classNames={{
              trigger:		controlBorder(props.invalid),
              value:		'text-[15px] text-[#1F2733]',
              listbox:		'px-0',
              popoverContent:	'px-0',
            }}
            onSelectionChange={(keys: any) => {
              const arr = [...keys];
              if (arr.length)
                props.setValue(String(arr[0]));
            }}>
      { props.options.map((opt: FieldInfo) =>
        <SelectItem key={opt.value} textValue={opt.desc}>
          { opt.desc }
        </SelectItem>
      )}
    </Select>
  </LabeledField>
);

// A labelled hairline that opens a sub-group inside a card body.
export interface GroupDividerProps {
  caption:		string;
  status?:		React.ReactNode;
}

export const GroupDivider = (props: GroupDividerProps) => (
  <FlexRow className="gap-[10px] w-full">
    <span className="text-[11px] font-semibold tracking-[0.6px] text-[#8A93A3] whitespace-nowrap">
      { props.caption }
    </span>
    { props.status }
    <div className="flex-1 h-[1px] bg-[#E6E9EF]" />
  </FlexRow>
);

export interface InfoBannerProps {
  text?:		string;
  tone?:		'info' | 'warn';
  children?:	React.ReactNode;
}

export const InfoBanner = (props: InfoBannerProps) => {
  const warn = props.tone === 'warn';

  return (
  <FlexRow align="start"
           className={cx(
             'gap-[10px] w-full rounded-[8px] p-[12px] border-solid border-1',
             warn	?	'bg-[#FDECEA] border-[#F3C9C4]'
              		:	'bg-[#F7F9FC] border-[#E6E9EF]',
           )}>
    <span className={cx(
            'material-icons !text-[18px] shrink-0',
            warn ? '!text-[#B14545]' : '!text-[#6B7280]',
          )}>
      { warn ? 'warning_amber' : 'info' }
    </span>
    <span className={cx(
            'text-[12px] leading-[17px]',
            warn ? 'text-[#B14545]' : 'text-[#5A6474]',
          )}>
      { props.children === undefined ? props.text : props.children }
    </span>
  </FlexRow>
  );
};

// Dashed full-width "add another one" affordance.
export interface AddRowProps {
  caption:		string;
  onClick:		() => any;
}

export const AddRow = (props: AddRowProps) => (
  <FlexRow justify="center"
           className={[
             'w-full rounded-[8px] py-[16px] gap-[8px]',
             'border-dashed border-1 border-[#C9D1DE]',
             'cursor-pointer select-none',
             'hover:bg-[#F7F9FC]',
           ].join(' ')}
           onClick={() => props.onClick()}>
    <span className="material-icons !text-[--Primetime-main] !text-[18px]">add</span>
    <span className="text-[14px] font-medium text-[--Primetime-main]">
      { props.caption }
    </span>
  </FlexRow>
);
